/**
 * Carga bajo demanda de hojas de estilo de terceros.
 *
 * Las hojas pesadas que sólo usan features lazy (AG Grid, Flatpickr, Leaflet) se declaran en
 * `angular.json` como bundles con `"inject": false`. El builder las emite con nombre ESTABLE
 * (sin hash) fuera de `styles.css`, y aquí se inyecta el `<link>` la primera vez que alguien
 * las necesita. Así el bundle inicial no arrastra ~210 kB de CSS que la mayoría de vistas
 * (login, inicio, perfil…) nunca usa.
 *
 * `loadLazyStyle` es una función suelta —no un servicio— a propósito: se llama también desde
 * efectos de módulo (ver `@shared/components/ag-grid`), donde todavía no hay inyector.
 */

/** Nombres de bundle declarados en `angular.json` → `build.options.styles[].bundleName`. */
export type LazyStyleBundle = 'ag-grid-theme' | 'flatpickr' | 'brand-palette';

const loaded = new Map<LazyStyleBundle, Promise<void>>();

/**
 * Inyecta la hoja si aún no está y resuelve cuando el navegador termina de aplicarla.
 * Idempotente: llamarla desde cada tabla/picker no duplica peticiones ni `<link>`.
 *
 * El `<link>` nunca se retira: una vez cargada la feature, quitarlo provocaría un flash de
 * contenido sin estilo al volver a entrar.
 */
export function loadLazyStyle(bundle: LazyStyleBundle): Promise<void> {
  const pending = loaded.get(bundle);
  if (pending) {
    return pending;
  }

  // Guarda para prerender/SSR: sin DOM no hay nada que inyectar.
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const promise = new Promise<void>((resolve) => {
    if (document.head.querySelector(`link[data-lazy-style="${bundle}"]`)) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = resolveHref(bundle);
    link.dataset['lazyStyle'] = bundle;
    // Resolvemos también en error: una hoja que no carga no debe dejar la vista colgada.
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
    document.head.appendChild(link);
  });

  loaded.set(bundle, promise);
  return promise;
}

/**
 * Resuelve la URL respetando el `<base href>` (la app se despliega bajo `/Partnercore/`),
 * porque el bundle se emite en la raíz de `dist/browser`.
 */
function resolveHref(bundle: LazyStyleBundle): string {
  const base = document.querySelector('base')?.getAttribute('href') ?? '/';
  return `${base.endsWith('/') ? base : base + '/'}${bundle}.css`;
}
